import type { ConnectorConnection, ConnectorSyncSummary, ImportRunResult } from '@hvac/contracts';

/**
 * The sentences the Connections page speaks in. Kept apart from the
 * components so they are tested on their own: a count that reads "1 invoices"
 * or a time that says "in 3 minutes" for the past is a small lie on a page
 * about trusting the numbers.
 */

const plural = (count: number, one: string, many = `${one}s`) =>
  `${count.toLocaleString('en-CA')} ${count === 1 ? one : many}`;

/** "just now", "12 minutes ago", "3 hours ago", "yesterday", "Oct 30". */
export function sinceLabel(iso: string | null, now: Date = new Date()): string {
  if (iso === null) return 'never';
  const then = new Date(iso);
  const minutes = Math.round((now.getTime() - then.getTime()) / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${plural(minutes, 'minute')} ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${plural(hours, 'hour')} ago`;
  if (hours < 48) return 'yesterday';
  return then.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
}

/** Invoices that are now in the books, new or matched to a record already there. */
export const invoicesIn = (summary: ConnectorSyncSummary): number =>
  summary.invoices.created + summary.invoices.linked;

/** One sentence for what a sync just did. */
export function syncSentence(name: string, summary: ConnectorSyncSummary): string {
  const parts: string[] = [];
  const customers = summary.customers.created;
  const jobs = summary.jobs.created;
  const invoices = invoicesIn(summary);
  if (customers > 0) parts.push(plural(customers, 'new customer'));
  if (jobs > 0) parts.push(plural(jobs, 'new job'));
  if (invoices > 0) parts.push(`${plural(invoices, 'invoice')} into the books`);
  if (summary.invoices.voided > 0)
    parts.push(`${plural(summary.invoices.voided, 'void')} reversed`);
  const refreshed = summary.customers.updated + summary.jobs.updated + summary.invoices.updated;
  if (refreshed > 0) parts.push(`${plural(refreshed, 'record')} refreshed`);
  const head =
    parts.length === 0
      ? `${name} is up to date; nothing had changed.`
      : `${name} synced: ${parts.join(', ')}.`;
  return summary.invoices.flagged > 0
    ? `${head} ${plural(summary.invoices.flagged, 'invoice')} need${summary.invoices.flagged === 1 ? 's' : ''} a look at the tax in Review.`
    : head;
}

/** The line under a connection's name. */
export function connectionLine(connection: ConnectorConnection, now: Date = new Date()): string {
  const account = connection.accountLabel === null ? '' : `${connection.accountLabel} · `;
  const key = connection.secretHint === null ? '' : `key ending ${connection.secretHint} · `;
  if (connection.lastSyncedAt === null) return `${account}${key}not synced yet`;
  return `${account}${key}synced ${sinceLabel(connection.lastSyncedAt, now)}`;
}

/** What an import did, for the last step of the import. */
export function importSentence(result: ImportRunResult): string {
  const parts: string[] = [];
  if (result.created > 0) parts.push(`${plural(result.created, 'invoice')} added as income`);
  if (result.linked > 0)
    parts.push(`${plural(result.linked, 'invoice')} matched to records already in the books`);
  if (result.updated > 0) parts.push(`${plural(result.updated, 'draft')} brought up to date`);
  if (result.voided > 0) parts.push(`${plural(result.voided, 'void')} reversed`);
  if (parts.length === 0) {
    return result.unchanged > 0
      ? 'Everything in this file was already in the books, exactly as it is. Nothing changed.'
      : 'Nothing in this file could be brought in.';
  }
  return `Done: ${parts.join(', ')}.`;
}
